import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Button, TextField, IconButton, Card, CardMedia, Dialog, DialogContent, Drawer, Divider
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { API_URLS } from '../config/api';
import { useCart } from '../contexts/CartContext';

function ProductPage() {
  const navigate = useNavigate();
  const [searchKeyword, setSearchKeyword] = useState('');
  const [previewImage, setPreviewImage] = useState('');
  const [previewProduct, setPreviewProduct] = useState(null);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
  
  // 从URL参数获取桌号
  const [searchParams] = useSearchParams();
  const tableNumber = searchParams.get('table');
  
  // 使用全局购物车状态
  const {
    cart,
    products,
    categories,
    selectedCategory,
    loading,
    totalPrice,
    totalCount,
    setSelectedCategory,
    setSelectedTableNumber,
    fetchProducts,
    addToCart,
    removeFromCart,
    clearCart,
    getCartItems
  } = useCart();

  // 路由守卫：检查URL参数中的桌号，如果没有则跳转回table页面
  React.useEffect(() => {
    if (!tableNumber) {
      navigate('/table', { replace: true });
    } else {
      // 如果有桌号，设置到全局状态中（用于订单页面）
      setSelectedTableNumber(tableNumber);
    }
  }, [tableNumber, navigate, setSelectedTableNumber]);

  // 如果没有桌号，显示加载状态
  if (!tableNumber) {
    return (
      <Box sx={{ 
        height: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <Typography sx={{ color: '#666' }}>正在跳转...</Typography>
      </Box>
    );
  }

  // 图片占位图
  const placeholderImg = `${process.env.PUBLIC_URL}/images/placeholder_image.svg`;

  // 搜索功能
  const handleSearch = (e) => {
    const keyword = e.target.value;
    setSearchKeyword(keyword);
    if (keyword.trim()) {
      fetchProducts(keyword);
    } else {
      fetchProducts();
    }
  };

  // 显示所有商品，因为已经按分类筛选获取
  const filteredProducts = products;

  // 图片预览
  const handleImageClick = (imageSrc, product) => {
    setPreviewImage(imageSrc);
    setPreviewProduct(product);
    setImageDialogOpen(true);
  };

  // 清空购物车并关闭抽屉
  const handleClearCart = () => {
    clearCart();
    setCartDrawerOpen(false);
  };

  // 跳转到结算页面
  const goToCheckout = () => {
    setCartDrawerOpen(false);
    navigate('/order');
  };

  return (
    <Box sx={{ 
      height: '100vh', 
      bgcolor: '#f5f5f5',
      fontFamily: 'PingFang SC, Microsoft YaHei, sans-serif',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      {/* 可滚动的全部内容区域 */}
      <Box sx={{ 
        flex: 1,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* 可滚动的顶部区域 - 搜索栏和Banner图 */}
        <Box>
          {/* 顶部搜索栏 */}
          <Box sx={{ 
            bgcolor: '#fff', 
            p: 2, 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            borderBottom: '1px solid #e0e0e0'
          }}>
            <IconButton 
              sx={{ p: 0 }}
              onClick={() => navigate('/table')}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box sx={{ 
              flex: 1, 
              bgcolor: '#f5f5f5', 
              borderRadius: 3, 
              px: 2, 
              py: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <SearchIcon sx={{ color: '#999' }} />
              <TextField
                value={searchKeyword}
                onChange={handleSearch}
                placeholder="请输入"
                variant="standard"
                InputProps={{
                  disableUnderline: true,
                  sx: { fontSize: 16, color: '#666' }
                }}
                sx={{ flex: 1 }}
              />
            </Box>
          </Box>

          {/* Banner图 */}
          <Box sx={{ p: 2, bgcolor: '#fff' }}>
            <Box sx={{ 
              width: '100%', 
              height: 180, 
              borderRadius: 2, 
              overflow: 'hidden',
              position: 'relative'
            }}>
              <img
                src={`${process.env.PUBLIC_URL}/images/banner_image_01.jpg`}
                alt="banner"
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'cover' 
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* 固定的店铺信息栏 */}
        <Box sx={{ 
          bgcolor: '#fff', 
          px: 2, 
          py: 1.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #e0e0e0',
          position: 'sticky',
          top: 0,
          zIndex: 10,
          flexShrink: 0
        }}>
          <Typography sx={{ fontSize: 18, fontWeight: 'bold', color: '#333' }}>
            麓Bar
          </Typography>
          <Box sx={{ 
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            backgroundColor: '#f8f9fa',
            borderRadius: 2,
            px: 2,
            py: 1,
            border: '1px solid #e9ecef'
          }}>
            <Box 
              component="span" 
              sx={{ 
                display: 'inline-block',
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: '#4caf50'
              }} 
            />
            <Typography sx={{ 
              fontSize: 14,
              fontWeight: '500',
              color: '#333'
            }}>
              桌号：{tableNumber}
            </Typography>
          </Box>
        </Box>

        {/* 主内容区 */}
        <Box sx={{ 
          display: 'flex', 
          bgcolor: '#fff', 
          flex: 1,
          minHeight: 'calc(100vh - 120px)'
        }}>
          {/* 左侧分类菜单 */}
          <Box sx={{ 
            width: { xs: 80, sm: 120 },
            bgcolor: '#f8f8f8', 
            borderRight: '1px solid #e0e0e0',
            py: 1,
            overflowY: 'auto',
            flexShrink: 0
          }}>
            {categories.map((category, index) => (
              <Button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                sx={{
                  width: '100%',
                  py: { xs: 1.5, sm: 2 },
                  px: { xs: 0.5, sm: 1 },
                  borderRadius: 0,
                  bgcolor: category.id === selectedCategory ? '#fff' : 'transparent',
                  color: category.id === selectedCategory ? '#333' : '#666',
                  fontSize: { xs: 12, sm: 14 },
                  fontWeight: category.id === selectedCategory ? 'bold' : 'normal',
                  borderLeft: category.id === selectedCategory ? '3px solid #1976d2' : '3px solid transparent',
                  justifyContent: 'flex-start',
                  minHeight: 'auto',
                  '&:hover': {
                    bgcolor: category.id === selectedCategory ? '#fff' : '#f0f0f0'
                  }
                }}
              >
                {category.name}
              </Button>
            ))}
          </Box>

          {/* 右侧商品列表 */}
          <Box sx={{ 
            flex: 1, 
            p: { xs: 1, sm: 2 },
            overflowY: 'auto',
            pb: '80px'
          }}>
            {/* 分类标题 */}
            <Typography sx={{ 
              fontSize: { xs: 16, sm: 18 },
              fontWeight: 'bold', 
              mb: { xs: 1, sm: 2 }, 
              color: '#333' 
            }}>
              {categories.find(cat => cat.id === selectedCategory)?.name || '商品列表'}
            </Typography>

            {loading ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography sx={{ color: '#999' }}>加载中...</Typography>
              </Box>
            ) : filteredProducts.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1, sm: 2 }, pb: 4 }}>
                {filteredProducts.map(product => (
                  <Card key={product.id} sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    boxShadow: 'none',
                    border: '1px solid #f0f0f0',
                    borderRadius: 2,
                    p: { xs: 1, sm: 2 },
                    position: 'relative'
                  }}>
                    <CardMedia
                      component="img"
                      image={product.preview ? `${API_URLS.DOWNLOAD_IMAGE}?view=${product.preview}` : placeholderImg}
                      alt={product.title}
                      loading="lazy"
                      onClick={() => handleImageClick(product.preview ? `${API_URLS.DOWNLOAD_IMAGE}?view=${product.preview}` : placeholderImg, product)}
                      sx={{ 
                        width: { xs: 60, sm: 80 },
                        height: { xs: 60, sm: 80 }, 
                        objectFit: 'cover',
                        borderRadius: 1,
                        mr: { xs: 1, sm: 2 },
                        cursor: 'pointer',
                        flexShrink: 0,
                        '&:hover': {
                          opacity: 0.8
                        }
                      }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ 
                        fontSize: { xs: 14, sm: 16 },
                        fontWeight: 'bold', 
                        color: '#333',
                        mb: { xs: 0.5, sm: 1 },
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {product.title}
                      </Typography>
                      <Typography sx={{ 
                        fontSize: { xs: 16, sm: 18 },
                        color: '#ff6b35',
                        fontWeight: 'bold'
                      }}>
                        ¥{product.price}{product.unit ? `/${product.unit}` : ''}
                      </Typography>
                    </Box>
                    
                    {/* 右侧数量控制按钮 */}
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: { xs: 0.5, sm: 1 },
                      flexShrink: 0
                    }}>
                      {(!cart[product.id] || cart[product.id] === 0) ? (
                        <IconButton 
                          onClick={() => addToCart(product.id)}
                          sx={{
                            bgcolor: '#333',
                            color: '#fff',
                            width: { xs: 32, sm: 36 },
                            height: { xs: 32, sm: 36 },
                            '&:hover': {
                              bgcolor: '#555'
                            }
                          }}
                        >
                          <AddIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                        </IconButton>
                      ) : (
                        <>
                          <IconButton 
                            onClick={() => removeFromCart(product.id)}
                            sx={{
                              bgcolor: '#333',
                              color: '#fff',
                              width: { xs: 28, sm: 32 },
                              height: { xs: 28, sm: 32 },
                              '&:hover': {
                                bgcolor: '#555'
                              }
                            }}
                          >
                            <RemoveIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
                          </IconButton>
                          
                          <Typography sx={{ 
                            minWidth: { xs: 20, sm: 30 },
                            textAlign: 'center',
                            fontSize: { xs: 14, sm: 16 },
                            fontWeight: 'bold'
                          }}>
                            {cart[product.id]}
                          </Typography>
                          
                          <IconButton 
                            onClick={() => addToCart(product.id)}
                            sx={{
                              bgcolor: '#333',
                              color: '#fff',
                              width: { xs: 28, sm: 32 },
                              height: { xs: 28, sm: 32 },
                              '&:hover': {
                                bgcolor: '#555'
                              }
                            }}
                          >
                            <AddIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  </Card>
                ))}
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography sx={{ color: '#999' }}>暂无商品</Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* 底部购物车 */}
      <Box sx={{ 
        position: 'fixed', 
        bottom: 0, 
        left: 0, 
        right: 0,
        bgcolor: '#fff',
        borderTop: '1px solid #e0e0e0',
        p: { xs: 1.5, sm: 2 },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box 
          sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
          onClick={() => setCartDrawerOpen(true)}
        >
          <Box sx={{ position: 'relative' }}>
            <ShoppingCartIcon sx={{ fontSize: 28, color: '#333' }} />
            {totalCount > 0 && (
              <Box sx={{
                position: 'absolute',
                top: -5,
                right: -5,
                bgcolor: '#ff4444',
                color: '#fff',
                borderRadius: '50%',
                width: 20,
                height: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 12
              }}>
                {totalCount}
              </Box>
            )}
          </Box>
          <Typography sx={{ 
            fontSize: { xs: 12, sm: 14 },
            color: '#666' 
          }}>
            ¥{totalPrice.toFixed(2)} 共{totalCount}件
          </Typography>
        </Box>
        
        <Button
          onClick={goToCheckout}
          sx={{
            bgcolor: '#333',
            color: '#fff',
            borderRadius: 3,
            px: { xs: 3, sm: 4 },
            py: { xs: 1, sm: 1.5 },
            fontSize: { xs: 14, sm: 16 },
            fontWeight: 'bold',
            '&:hover': {
              bgcolor: '#555'
            }
          }}
        >
          去结算
        </Button>
      </Box>

      {/* 图片预览对话框 */}
      <Dialog 
        open={imageDialogOpen} 
        onClose={() => setImageDialogOpen(false)}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 3,
            overflow: 'hidden'
          }
        }}
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          <IconButton
            onClick={() => setImageDialogOpen(false)}
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              bgcolor: 'rgba(0,0,0,0.5)',
              color: '#fff',
              zIndex: 2,
              '&:hover': {
                bgcolor: 'rgba(0,0,0,0.7)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
          
          <Box sx={{ position: 'relative' }}>
            <img
              src={previewImage}
              alt="商品预览"
              style={{
                width: '100%',
                height: 'auto',
                maxHeight: '60vh',
                objectFit: 'contain',
                display: 'block'
              }}
            />
          </Box>
          
          {previewProduct && (
            <Box sx={{ 
              p: 3, 
              bgcolor: '#fff',
              borderTop: '1px solid #f0f0f0'
            }}>
              <Typography sx={{ 
                fontSize: 20, 
                fontWeight: 'bold', 
                color: '#333',
                mb: 2,
                lineHeight: 1.4
              }}>
                {previewProduct.title}
              </Typography>
              
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 2
              }}>
                <Typography sx={{ 
                  fontSize: 24, 
                  color: '#ff6b35',
                  fontWeight: 'bold'
                }}>
                  ¥{previewProduct.price}{previewProduct.unit ? `/${previewProduct.unit}` : ''}
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {(!cart[previewProduct.id] || cart[previewProduct.id] === 0) ? (
                    <Button 
                      onClick={() => {
                        addToCart(previewProduct.id);
                        setImageDialogOpen(false);
                      }}
                      sx={{
                        bgcolor: '#333',
                        color: '#fff',
                        borderRadius: 2,
                        px: 3,
                        py: 1.5,
                        fontSize: 16,
                        fontWeight: 'bold',
                        '&:hover': {
                          bgcolor: '#555'
                        }
                      }}
                    >
                      加入购物车
                    </Button>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IconButton 
                        onClick={() => removeFromCart(previewProduct.id)}
                        sx={{
                          bgcolor: '#333',
                          color: '#fff',
                          width: 40,
                          height: 40,
                          '&:hover': {
                            bgcolor: '#555'
                          }
                        }}
                      >
                        <RemoveIcon />
                      </IconButton>
                      
                      <Typography sx={{ 
                        minWidth: 40, 
                        textAlign: 'center',
                        fontSize: 18,
                        fontWeight: 'bold'
                      }}>
                        {cart[previewProduct.id]}
                      </Typography>
                      
                      <IconButton 
                        onClick={() => addToCart(previewProduct.id)}
                        sx={{
                          bgcolor: '#333',
                          color: '#fff',
                          width: 40,
                          height: 40,
                          '&:hover': {
                            bgcolor: '#555'
                          }
                        }}
                      >
                        <AddIcon />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              </Box>
              
              {previewProduct.description && (
                <Typography sx={{ 
                  fontSize: 14, 
                  color: '#666',
                  mt: 2,
                  lineHeight: 1.6
                }}>
                  {previewProduct.description}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* 购物车抽屉 */}
      <Drawer
        anchor="bottom"
        open={cartDrawerOpen}
        onClose={() => setCartDrawerOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: '80vh'
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mb: 2
          }}>
            <Typography sx={{ fontSize: 18, fontWeight: 'bold', color: '#333' }}>
              购物车
            </Typography>
            <Button
              onClick={handleClearCart}
              startIcon={<DeleteIcon />}
              sx={{
                color: '#999',
                fontSize: 14,
                '&:hover': {
                  color: '#666'
                }
              }}
            >
              清空购物车
            </Button>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Box sx={{ maxHeight: '50vh', overflowY: 'auto' }}>
            {getCartItems().length > 0 ? (
              getCartItems().map(item => (
                <Box key={item.id} sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  py: 2,
                  borderBottom: '1px solid #f0f0f0'
                }}>
                  <CardMedia
                    component="img"
                    image={item.preview ? `${API_URLS.DOWNLOAD_IMAGE}?view=${item.preview}` : placeholderImg}
                    alt={item.title}
                    loading="lazy"
                    sx={{ 
                      width: 60, 
                      height: 60, 
                      objectFit: 'cover',
                      borderRadius: 1,
                      mr: 2
                    }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ 
                      fontSize: 16, 
                      fontWeight: 'bold', 
                      color: '#333',
                      mb: 0.5
                    }}>
                      {item.title}
                    </Typography>
                    <Typography sx={{ 
                      fontSize: 16, 
                      color: '#ff6b35',
                      fontWeight: 'bold'
                    }}>
                      ¥{item.price}{item.unit ? `/${item.unit}` : ''}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton 
                      onClick={() => removeFromCart(item.id)}
                      sx={{
                        bgcolor: '#333',
                        color: '#fff',
                        width: 32,
                        height: 32,
                        '&:hover': {
                          bgcolor: '#555'
                        }
                      }}
                    >
                      <RemoveIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                    
                    <Typography sx={{ 
                      minWidth: 30, 
                      textAlign: 'center',
                      fontSize: 16,
                      fontWeight: 'bold'
                    }}>
                      {item.count}
                    </Typography>
                    
                    <IconButton 
                      onClick={() => addToCart(item.id)}
                      sx={{
                        bgcolor: '#333',
                        color: '#fff',
                        width: 32,
                        height: 32,
                        '&:hover': {
                          bgcolor: '#555'
                        }
                      }}
                    >
                      <AddIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                </Box>
              ))
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography sx={{ color: '#999', fontSize: 16 }}>
                  购物车是空的
                </Typography>
              </Box>
            )}
          </Box>

          {totalCount > 0 && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              pt: 2,
              mt: 2,
              borderTop: '1px solid #f0f0f0'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ShoppingCartIcon sx={{ fontSize: 24, color: '#333' }} />
                <Typography sx={{ fontSize: 16, color: '#333' }}>
                  ¥{totalPrice.toFixed(2)} 共{totalCount}件
                </Typography>
              </Box>
              
              <Button
                onClick={goToCheckout}
                sx={{
                  bgcolor: '#333',
                  color: '#fff',
                  borderRadius: 3,
                  px: 4,
                  py: 1.5,
                  fontSize: 16,
                  fontWeight: 'bold',
                  '&:hover': {
                    bgcolor: '#555'
                  }
                }}
              >
                去结算
              </Button>
            </Box>
          )}
        </Box>
      </Drawer>
    </Box>
  );
}

export default ProductPage; 